/**
 * FeedStyleArticles · iter169 (2026-02-27)
 *
 * Rendert News/FAQ-Artikel im Feed-Card-Stil (wie Social-Feed-Posts).
 * Gepinnte Artikel zuerst mit Pin-Badge oben rechts.
 * Autor = "stilltouched" (Plattform), Avatar = Brand-Logo.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Pin } from "lucide-react";
import Brand from "@/components/common/Brand";

function areaLabelText(area) {
  return { MARKETPLACE: "Marketplace", STILLTOUCHEDX: "stilltouchedX", SOCIAL: "Social" }[area] || area;
}

export default function FeedStyleArticles({ items, area, hasMore = false, onLoadMore }) {
  const { t } = useTranslation();
  const sentinelRef = useRef(null);

  // IntersectionObserver für Infinite-Scroll (iter170)
  useEffect(() => {
    if (!hasMore || !onLoadMore) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) onLoadMore();
        });
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, items?.length]);

  if (!items || items.length === 0) {
    return (
      <div data-testid="feed-articles-empty" className="text-sm text-[#666] text-center py-16">
        Aktuell keine Einträge in diesem Bereich.
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="feed-articles-list">
      {items.map((a) => (
        <ArticleCard key={a.article_id} article={a} area={area} />
      ))}
      {hasMore && (
        <div
          ref={sentinelRef}
          data-testid="feed-articles-load-more-sentinel"
          className="text-sm text-[#666] text-center py-6"
        >
          <div className="inline-block w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
          <div className="mt-2 text-xs text-[#999]">{t("common.loading_more_entries")}</div>
        </div>
      )}
    </div>
  );
}

function ArticleCard({ article, area }) {
  const isPinned = !!article.is_pinned;
  const platformAvatar = article.platform_avatar_url || "";
  const dateStr = new Date(article.updated_at || article.created_at).toLocaleDateString("de-DE", {
    day: "2-digit", month: "long", year: "numeric",
  });
  return (
    <article
      data-testid={`feed-article-${article.article_id}`}
      className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden"
    >
      {/* Header — Autor + Bereich + Datum + Pin-Badge */}
      <div className="flex items-center gap-3 px-4 pt-3 pb-2">
        {platformAvatar ? (
          <img
            src={platformAvatar}
            alt="stilltouched"
            data-testid={`feed-article-avatar-${article.article_id}`}
            className="w-10 h-10 rounded-full object-cover shrink-0 border border-[#E5E5E5]"
            loading="lazy"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#0A0A0A] flex items-center justify-center text-white text-xs font-bold shrink-0">
            <Brand className="text-[10px] leading-none" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Brand variant="on-light" className="text-sm truncate" />
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F5F5F5] text-[#666] uppercase tracking-wider font-semibold">
              {areaLabelText(area)}
            </span>
          </div>
          <div className="text-xs text-[#999]">{dateStr}</div>
        </div>
        {isPinned && (
          <div
            data-testid={`feed-article-pin-${article.article_id}`}
            className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-[10px] font-semibold uppercase tracking-wider"
          >
            <Pin size={10} /> Angepinnt
          </div>
        )}
      </div>

      {/* Titel + Body */}
      <div className="px-4 pb-4">
        <h3 className="text-base sm:text-lg font-bold text-[#0A0A0A] mb-2 leading-snug">
          {article.title}
        </h3>
        {/* iter169.4 · News-Bild (16:9), nur wenn media_url gesetzt */}
        {article.media_url && (
          <div className="mb-3 rounded-xl overflow-hidden bg-[#F5F5F5] aspect-video">
            <img
              src={article.media_url}
              alt={article.title || ""}
              data-testid={`feed-article-media-${article.article_id}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        )}
        <div
          className="prose prose-neutral prose-sm max-w-none
                     prose-headings:font-bold prose-headings:text-[#0A0A0A]
                     prose-a:text-[#0A0A0A] prose-a:underline
                     prose-img:rounded-xl prose-img:max-h-[420px] prose-img:w-auto"
          dangerouslySetInnerHTML={{ __html: article.body || "" }}
        />
      </div>
    </article>
  );
}
